import { css, keyframes } from '@emotion/core';
import { colorsPalette } from '../../../styles/colors';
import { typography, fontWeights } from '../../../styles/typography';
import { MediaQueries } from '../../../styles/Grid';
import {
  NAV_MAX_HEIGHT,
  NAV_MAX_HEIGHT_SMALL_VIEWPORT,
} from '../../../utils/constants';

const PANEL_SIZES = {
  sm: '100%',
  md: 580,
  lg: 603,
  xl: 627,
};

export const mainContainer = css({
  position: 'relative',
  height: `calc(100vh - (${NAV_MAX_HEIGHT_SMALL_VIEWPORT}px))`,
  [MediaQueries.mdAndUp]: {
    height: `calc(100vh - (${NAV_MAX_HEIGHT}px))`,
  },
});

const PROGRESS_BAR_HEIGHT_SMALL_VIEWPORT = 103;
const PROGRESS_BAR_HEIGHT = 135;

export const container = css({
  overflowY: 'scroll',
  height: `calc(100vh - (${
    NAV_MAX_HEIGHT_SMALL_VIEWPORT + PROGRESS_BAR_HEIGHT_SMALL_VIEWPORT
  }px))`,
  [MediaQueries.mdAndUp]: {
    height: `calc(100vh - (${NAV_MAX_HEIGHT + PROGRESS_BAR_HEIGHT}px))`,
  },
});

const headlineFont = css({
  ...typography.effra22,
  fontWeight: fontWeights.medium,
  [MediaQueries.mdAndUp]: {
    ...typography.effra40,
    fontWeight: fontWeights.medium,
  },
});

export const headline = css(headlineFont, {
  color: colorsPalette.black,
  textAlign: 'center',
  marginBottom: 8,
  marginTop: 16,
  [MediaQueries.lgAndUp]: {
    marginTop: 24,
  },
});

const descriptionFont = css({
  ...typography.effra16,
  [MediaQueries.lgAndUp]: {
    ...typography.effra18,
    lineHeight: 1.67,
  },
});

export const description = css(descriptionFont, {
  textAlign: 'center',
  marginBottom: 23,
  [MediaQueries.lgAndUp]: {
    fontSize: 18,
    marginBottom: 17,
  },
  [MediaQueries.lgAndUp]: {
    marginBottom: 31,
  },
});

export const secondaryCTAContainer = css({
  textAlign: 'center',
  marginBottom: 33,
});

export const secondaryCTAButton = css(typography.effra16, {
  background: colorsPalette.transparent,
  color: colorsPalette.grey60,
  border: 0,
  borderBottom: `1px solid ${colorsPalette.grey20}`,
  padding: 0,
  cursor: 'pointer',
});

const helpMessageFadeIn = keyframes`
  from { opacity: 0 }
  to { opacity: 1 }
`;

export const helpMessageContainer = css({
  display: 'none',
  opacity: 0,
  textAlign: 'center',
  position: 'fixed',
  zIndex: 1,
  width: '100%',
  bottom: 127,
  animation: `${helpMessageFadeIn} 0.5s 0.7s forwards`,
  [MediaQueries.mdAndUp]: {
    bottom: 159,
  },
});

export const sidePanelSpacing = css({
  padding: '70px 24px 110px 24px',
  width: PANEL_SIZES.sm,
  [MediaQueries.mdAndUp]: {
    paddingLeft: 32,
    paddingRight: 40,
    paddingTop: 48,
    width: PANEL_SIZES.md,
  },
  [MediaQueries.lgAndUp]: {
    paddingRight: 78,
    width: PANEL_SIZES.lg,
  },
  [MediaQueries.xl]: {
    paddingRight: 82,
    width: PANEL_SIZES.xl,
  },
});

export const helpMessageVisible = css({
  display: 'block',
});

export const helpMessage = css(typography.effra14, {
  display: 'inline-block',
  backgroundColor: colorsPalette.white,
  color: colorsPalette.teal,
  border: `1px solid ${colorsPalette.grey10}`,
  borderRadius: 4,
  padding: `8px 16px`,
});

export const sidePanel = {
  section: {
    paddingTop: 36,
    paddingBottom: 32,
    borderBottom: `1px solid ${colorsPalette.grey10}`,

    ':first-of-type': {
      paddingTop: 0,
    },

    [MediaQueries.mdAndUp]: {
      paddingTop: 32,
    },
  },

  subtitle: {
    ...typography.effra22,
    letterSpacing: '-0.004em',
    marginBottom: 8,
    [MediaQueries.mdAndUp]: {
      marginBottom: 4,
    },
  },

  paragraph: { ...typography.effra16 },

  unorderedList: {
    marginTop: 24,
    paddingTop: 0,
    paddingBottom: 0,

    li: {
      ...typography.effra16,
      display: 'block',
      marginBottom: 8,
      paddingLeft: 30,
      position: 'relative',

      '&:before': {
        content: '"\\e915"', // Checkmark
        fontFamily: 'icomoon',
        fontSize: 10,
        color: colorsPalette.red,
        position: 'absolute',
        top: 5,
        left: 0,
      },

      ':last-child': {
        marginBottom: 0,
      },
    },
  },
};

sidePanel.richText = {
  'h1, h2, h3, h4, h5, h6': sidePanel.subtitle,
  p: sidePanel.paragraph,
  ul: sidePanel.unorderedList,
};
